import { SceneManager } from "scene-manager";
import { Game } from "../Game";
import { PeskyMolesContext } from "./composition/PeskyMolesContext";
import { PreloaderScene } from "./scenes/preloader/PreloaderScene";
import { CanvasScaler, Scales } from "../canvas/CanvasScaler";
import { PerspectiveCamera, Scene, WebGLRenderer as ThreeRenderer, VSMShadowMap } from "three";
import { GLTFLoader } from "../loaders/GLTFLoader";
import { extensions, Renderer } from "pixi.js";
import { RenderCamera } from "./three/RenderCamera";
import { PeskyMolesViewableLayer } from "./layers/PeskyMolesViewableLayer";
import { ThreeJSTextureLoader } from "../loaders/ThreeJSTextureLoader";
import { QuarksParticlesLoader } from "../loaders/QuarksParticlesLoader";
import { ThreeFontLoader } from "../loaders/ThreeFontLoader";
import { OrbitOverLife } from "three.quarks";

export class PeskyMoles extends Game {
    private readonly context: PeskyMolesContext;
    private readonly sceneManager: SceneManager;
    private readonly canvasScaler: CanvasScaler;

    private readonly threeRenderer: ThreeRenderer;
    private readonly threeScene: Scene;
    private readonly renderCamera: RenderCamera;

    public constructor() {
        super();
        this.fixQuarkThree();
        const webglContext = this.view.getContext("webgl");
        const webglContext2 = this.view.getContext("webgl2");
        this.threeRenderer = new ThreeRenderer({
            context: webglContext ?? webglContext2,
            // antialias: true,
            stencil: true
        });
        this.threeRenderer.setPixelRatio(window.devicePixelRatio);
        this.threeRenderer.setSize(window.innerWidth, window.innerHeight);
        this.threeRenderer.setClearColor(0xb6ffab, 1);
        // Enable shadows in 3d renderer
        this.threeRenderer.shadowMap.enabled = true;
        this.threeRenderer.shadowMap.type = VSMShadowMap;

        this.threeScene = new Scene();
        this.renderCamera = new RenderCamera(new PerspectiveCamera(50, window.innerWidth / window.innerHeight));

        this.canvasScaler = new CanvasScaler(this.renderer, [
            new Scales(1080, 2400),
            new Scales(2880, 1620),
            new Scales(2400, 1800)
        ]);
        this.sceneManager = new SceneManager(
            this.stage,
            this.threeScene,
            {
                viewableLayerType: new PeskyMolesViewableLayer(this.canvasScaler)
            }
        );
        this.context = new PeskyMolesContext();

        this.setupRenderingLoop();
        this.canvasScaler.onResize.add(this.onResize, this);

        const gltfLoader = new GLTFLoader();
        extensions.add(gltfLoader);
        const quarksParticlesLoader = new QuarksParticlesLoader();
        extensions.add(quarksParticlesLoader);
        const textureLoader = new ThreeJSTextureLoader();
        extensions.add(textureLoader);
        const threeFontLoader = new ThreeFontLoader();
        extensions.add(threeFontLoader);

        (globalThis as any).__PIXI_APP__ = this;
        this.setupCustomMousePointer();
    }

    public override async launch(manifestPath: string): Promise<void> {
        await super.launch(manifestPath);
        this.context.build(this.canvasScaler, this.sceneManager, this.renderCamera, this.renderer as Renderer);
        await this.sceneManager.showViewableAsync(await this.context.resolve(PreloaderScene));
    }

    private setupRenderingLoop(): void {
        const render = () => {
            this.threeRenderer.resetState();
            this.threeRenderer.render(this.threeScene, this.renderCamera.current);

            this.renderer.reset();
            this.renderer.render(this.stage);

            requestAnimationFrame(render);
        }
        requestAnimationFrame(render);
    }

    private onResize(_scales: Scales): void {
        this.threeRenderer.setSize(window.innerWidth, window.innerHeight);
        this.renderCamera.aspect = window.innerWidth / window.innerHeight;
        this.renderCamera.current.updateProjectionMatrix();
    }

    private setupCustomMousePointer(): void {
        // Css style for icons
        const defaultIcon = "url('./assets/cursors/main.png') 16 16, auto";
        const hoverIcon = "url('./assets/cursors/pointer.png') 4 4, auto";

        // Add custom cursor styles
        this.renderer.events.cursorStyles.default = defaultIcon;
        this.renderer.events.cursorStyles.hover = hoverIcon;
    }

    private fixQuarkThree(): void {
        OrbitOverLife.prototype.clone = function() {
            return new OrbitOverLife(this.orbitSpeed.clone(), this.axis.clone());
        }
    }

}